import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useDropzone } from 'react-dropzone';
import {
  ArrowLeft,
  Save,
  X,
  Image as ImageIcon,
} from 'lucide-react';
import api from '../services/api';
import Toast from '../components/Toast/Toast';
import '../styles/library-page.css';

const inputClassName =
  'w-full px-3 py-2 border border-white/20 rounded-md shadow-sm focus:outline-none focus:ring-gold-50 focus:border-gold-50 bg-black/50 text-white backdrop-blur-sm placeholder-gray-500 text-sm';

const getFieldClassName = (hasError) =>
  `${inputClassName}${hasError ? ' border-red-500 focus:border-red-500 focus:ring-red-500/50' : ''}`;

const getDropzoneClassName = (isDragActive, hasError) => {
  if (isDragActive) return 'border-gold-50 bg-gold-50/10';
  if (hasError) return 'border-red-500/60 hover:border-red-400';
  return 'border-white/20 hover:border-gold-50/60';
};

const labelClassName = 'block text-sm font-medium text-gray-300 mb-2';

const FormField = ({ label, error, children, className = '' }) => (
  <div className={className}>
    <label className={`${labelClassName}${error ? ' text-red-400' : ''}`}>{label}</label>
    {children}
    {error && <p className="text-sm text-red-400 mt-1">{error.message}</p>}
  </div>
);

const validateOptionalUrl = (value) => {
  if (!value || !String(value).trim()) return true;
  try {
    // eslint-disable-next-line no-new
    new URL(value);
    return true;
  } catch {
    return 'Please enter a valid URL';
  }
};

const validateImageDimensions = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      if (img.width < 400 || img.height < 400) {
        reject(new Error('Image must be at least 400px by 400px'));
        return;
      }
      resolve();
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Unable to read image file'));
    };
    img.src = url;
  });

const CreateArtist = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageTouched, setImageTouched] = useState(false);
  const [imageError, setImageError] = useState(null);
  const [showMessage, setShowMessage] = useState({ show: false, type: '', message: '' });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    mode: 'onTouched',
    reValidateMode: 'onChange',
    defaultValues: {
      artist_name: '',
      artist_legal_name: '',
      artist_fb_url: '',
      artist_ig_url: '',
      bio: '',
    },
  });

  const dismissMessage = () => setShowMessage({ show: false, type: '', message: '' });

  const processImageFile = async (file) => {
    if (!file.type.startsWith('image/')) {
      setImageTouched(true);
      setImageError('Please select a valid image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setImageTouched(true);
      setImageError('Image file size must be less than 5MB');
      return;
    }

    try {
      await validateImageDimensions(file);
      setImageFile(file);
      setImageError(null);

      const reader = new FileReader();
      reader.onload = (event) => setImagePreview(event.target.result);
      reader.readAsDataURL(file);
    } catch (error) {
      setImageTouched(true);
      setImageError(error.message || 'Invalid image file');
    }
  };

  const onImageDrop = (acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      processImageFile(acceptedFiles[0]);
    }
  };

  const {
    getRootProps: getImageRootProps,
    getInputProps: getImageInputProps,
    isDragActive: isImageDragActive,
  } = useDropzone({
    onDrop: onImageDrop,
    accept: {
      'image/*': ['.jpg', '.jpeg', '.png', '.webp', '.gif'],
    },
    multiple: false,
  });

  const imageRootProps = getImageRootProps();

  const handleImageBlur = (event) => {
    imageRootProps.onBlur?.(event);
    setImageTouched(true);
    if (!imageFile) {
      setImageError('Artist image is required');
    }
  };

  const showImageError = imageTouched && imageError;

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (imageTouched) {
      setImageError('Artist image is required');
    }
  };

  const validateImageField = () => {
    if (!imageFile) {
      setImageTouched(true);
      setImageError('Artist image is required');
    }
  };

  const onSubmit = async (data) => {
    if (!imageFile) {
      setImageTouched(true);
      setImageError('Artist image is required');
      return;
    }

    setLoading(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('artist_name', data.artist_name.trim());
      formDataToSend.append('artist_legal_name', data.artist_legal_name.trim());
      formDataToSend.append('artist_fb_url', data.artist_fb_url.trim() || '');
      formDataToSend.append('artist_ig_url', data.artist_ig_url.trim() || '');
      formDataToSend.append('bio', data.bio.trim());
      formDataToSend.append('image', imageFile);

      const response = await api.post('/api/artists', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        setShowMessage({
          show: true,
          type: 'success',
          message: 'Artist submitted successfully! It is now pending admin approval. Redirecting to submissions...',
        });

        reset();
        setImageFile(null);
        setImagePreview(null);
        setImageTouched(false);
        setImageError(null);

        setTimeout(() => {
          navigate('/submissions');
        }, 2000);
      }
    } catch (error) {
      console.error('Failed to create artist:', error);
      setShowMessage({
        show: true,
        type: 'error',
        message: error.response?.data?.error || 'Failed to create artist. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="library-page library-page--transparent">
      <div className="library-page-header relative overflow-hidden group">
        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/[0.095] to-transparent opacity-0 group-hover:opacity-100 transition-all duration-700 transform -translate-x-full group-hover:translate-x-full" />
        <div className="relative z-10 p-6">
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-white/10 transition-colors flex-shrink-0"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-white">Create New Artist</h1>
              <p className="text-sm text-gray-400 mt-1">
                Add a new artist to the MediaBlox platform
              </p>
            </div>
          </div>
        </div>
      </div>

      <Toast
        show={showMessage.show}
        type={showMessage.type}
        message={showMessage.message}
        onDismiss={dismissMessage}
        action={
          showMessage.type === 'success' ? (
            <button
              type="button"
              onClick={() => navigate('/submissions')}
              className="flex-shrink-0 rounded-md bg-white/20 px-3 py-1 text-xs font-medium text-white transition-colors hover:bg-white/30"
            >
              View Submissions
            </button>
          ) : null
        }
      />

      <div className="library-page-scroll">
        <div className="library-page-panel">
          <form onSubmit={handleSubmit(onSubmit, validateImageField)} className="library-page-form">
            <section className="library-page-form-section">
              <h2 className="text-lg font-semibold text-white border-b border-white/10 pb-2 mb-6">
                Artist Information
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField label="Artist Name *" error={errors.artist_name} className="md:col-span-2">
                  <input
                    type="text"
                    {...register('artist_name', { required: 'Artist name is required' })}
                    className={getFieldClassName(Boolean(errors.artist_name))}
                    placeholder="Enter artist name"
                  />
                </FormField>

                <FormField label="Legal Name *" error={errors.artist_legal_name} className="md:col-span-2">
                  <input
                    type="text"
                    {...register('artist_legal_name', { required: 'Legal name is required' })}
                    className={getFieldClassName(Boolean(errors.artist_legal_name))}
                    placeholder="Enter legal name"
                  />
                </FormField>

                <FormField label="Facebook URL" error={errors.artist_fb_url}>
                  <input
                    type="url"
                    {...register('artist_fb_url', { validate: validateOptionalUrl })}
                    className={getFieldClassName(Boolean(errors.artist_fb_url))}
                    placeholder="https://facebook.com/..."
                  />
                </FormField>

                <FormField label="Instagram URL" error={errors.artist_ig_url}>
                  <input
                    type="url"
                    {...register('artist_ig_url', { validate: validateOptionalUrl })}
                    className={getFieldClassName(Boolean(errors.artist_ig_url))}
                    placeholder="https://instagram.com/..."
                  />
                </FormField>

                <FormField label="Biography *" error={errors.bio} className="md:col-span-2">
                  <textarea
                    {...register('bio', {
                      required: 'Biography is required',
                      validate: (value) =>
                        String(value || '').trim() ? true : 'Biography is required',
                    })}
                    rows={4}
                    className={getFieldClassName(Boolean(errors.bio))}
                    placeholder="Enter artist biography..."
                  />
                </FormField>
              </div>
            </section>

            <section className="library-page-form-section">
              <h2 className="text-lg font-semibold text-white border-b border-white/10 pb-2 mb-6">
                Artist Image
              </h2>

              <FormField
                label="Artist Image *"
                error={showImageError ? { message: imageError } : null}
              >
                {imagePreview && (
                  <div className="relative inline-block mb-4">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-48 h-48 object-cover rounded-full border-2 border-white/20"
                    />
                    <button
                      type="button"
                      onClick={removeImage}
                      className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                )}

                <div
                  {...imageRootProps}
                  onBlur={handleImageBlur}
                  tabIndex={0}
                  className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors outline-none ${getDropzoneClassName(isImageDragActive, showImageError)}`}
                >
                  <input {...getImageInputProps()} />
                  <ImageIcon className="mx-auto h-12 w-12 text-gray-500 mb-4" />
                  {imageFile ? (
                    <div>
                      <p className="text-sm font-medium text-white">{imageFile.name}</p>
                      <p className="text-xs text-gray-400">
                        {(imageFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm text-gray-400">
                        {isImageDragActive
                          ? 'Drop the image here'
                          : 'Drag & drop an artist image here, or click to select'}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        PNG, JPG, GIF or WEBP up to 5MB · minimum 400×400px
                      </p>
                    </div>
                  )}
                </div>
              </FormField>
            </section>

            <div className="flex justify-end gap-4 mt-8 pt-6 border-t border-white/10">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="px-6 py-2 border border-white/20 rounded-md text-sm font-medium text-gray-300 bg-black/50 hover:bg-black/70 focus:outline-none focus:ring-2 focus:ring-gold-50 focus:border-gold-50 transition-colors backdrop-blur-sm"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center px-6 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-gold-50 hover:bg-gold-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Create Artist
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateArtist;
